/** Word-level diff for long text fields (descriptions) in edit reviews. */

export type DiffPart = { kind: 'same' | 'add' | 'del'; text: string }

/**
 * Longest-common-subsequence diff over words (whitespace kept attached). Falls back to a single
 * del + add when the texts are too long to diff cheaply in the browser.
 */
export function wordDiff(a: string, b: string, maxTokens = 3000): DiffPart[] {
  const ta = a.split(/(\s+)/).filter(Boolean)
  const tb = b.split(/(\s+)/).filter(Boolean)
  if (ta.length * tb.length > maxTokens * maxTokens) {
    return [...(a ? [{ kind: 'del' as const, text: a }] : []), ...(b ? [{ kind: 'add' as const, text: b }] : [])]
  }
  const n = ta.length
  const m = tb.length
  // dp[i][j] = LCS length of ta[i..] and tb[j..], in one flat array.
  const dp = new Uint32Array((n + 1) * (m + 1))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i * (m + 1) + j] =
        ta[i] === tb[j]
          ? dp[(i + 1) * (m + 1) + j + 1]! + 1
          : Math.max(dp[(i + 1) * (m + 1) + j]!, dp[i * (m + 1) + j + 1]!)
    }
  }
  const out: DiffPart[] = []
  const push = (kind: DiffPart['kind'], text: string) => {
    const last = out[out.length - 1]
    if (last && last.kind === kind) last.text += text
    else out.push({ kind, text })
  }
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (ta[i] === tb[j]) {
      push('same', ta[i]!)
      i++
      j++
    } else if (dp[(i + 1) * (m + 1) + j]! >= dp[i * (m + 1) + j + 1]!) {
      push('del', ta[i++]!)
    } else {
      push('add', tb[j++]!)
    }
  }
  while (i < n) push('del', ta[i++]!)
  while (j < m) push('add', tb[j++]!)
  return out
}

/** Split two lists into removed / kept / added, preserving order (after's order for kept/added). */
export function listDiff(before: string[], after: string[]): Array<{ kind: 'same' | 'add' | 'del'; value: string }> {
  const a = new Set(before)
  const b = new Set(after)
  return [
    ...after.map((v) => ({ kind: a.has(v) ? ('same' as const) : ('add' as const), value: v })),
    ...before.filter((v) => !b.has(v)).map((v) => ({ kind: 'del' as const, value: v })),
  ]
}
